import React, { memo, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { dates, formats, validators, arrays } from 'investira.sdk';
import {
  Stack,
  Card,
  CardHeader,
  CardContent,
  Icon,
  Typography,
  Divider,
  Container,
  Skeleton
} from 'investira.react.components';
import { StocksChart } from 'investira.react.charts';
import { COTACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import { ScrollContainer } from '../../../../../components/molecules';
import {
  INDICADOR_DISPLAY_MODE,
  getIndicadorDisplayConfig
} from '../../../../../components/organisms/indicadorDisplayMode';
import Controls from './Controls';
import {
  formatIndicadorDisplayValue,
  formatIndicadorCotacaoData,
  getIndicadorCotacaoResumo
} from '../_shared/indicadoresCotacao.helpers';

const IndicadoresCotacao = memo(props => {
  const {
    cotacoes,
    cenarios,
    isLoading = false,
    displayMode = INDICADOR_DISPLAY_MODE.outros
  } = props;
  const xDisplayConfig = getIndicadorDisplayConfig(displayMode);
  const xHistorico = cotacoes?.[0]?.data || [];
  const xResumo = useMemo(
    () => getIndicadorCotacaoResumo(xHistorico, xDisplayConfig),
    [xHistorico, xDisplayConfig]
  );
  const chartRef = useRef(null);
  const xResumoCotacao = xResumo
    ? [
        {
          id: 'atual',
          values: [
            {
              id: 'atual',
              description: formats.formatDate(xResumo.dataAtual),
              currentValue: formatIndicadorDisplayValue(xResumo.atual, xDisplayConfig)
            }
          ]
        },
        {
          id: 'minimo-12-meses',
          label: 'Min. 12 meses',
          values: [
            {
              id: 'minimo-12-meses',
              currentValue: formatIndicadorDisplayValue(xResumo.minimo12m, xDisplayConfig)
            }
          ]
        },
        {
          id: 'maximo-12-meses',
          label: 'Máx. 12 meses',
          values: [
            {
              id: 'maximo-12-meses',
              currentValue: formatIndicadorDisplayValue(xResumo.maximo12m, xDisplayConfig)
            }
          ]
        },
        ...(!validators.isNull(xResumo.variacao12m)
          ? [
              {
                id: 'variacao-12-meses',
                label: `${xDisplayConfig.variationLabel} em 12 meses`,
                values: [
                  {
                    id: 'variacao-12-meses',
                    currentValue: formatIndicadorDisplayValue(
                      xResumo.variacao12m,
                      xDisplayConfig,
                      true
                    )
                  }
                ]
              }
            ]
          : []),
        ...(!validators.isNull(xResumo.variacaoInicio)
          ? [
              {
                id: 'variacao-inicio',
                label: `${xDisplayConfig.variationLabel} do início`,
                values: [
                  {
                    id: 'variacao-inicio',
                    currentValue: formatIndicadorDisplayValue(
                      xResumo.variacaoInicio,
                      xDisplayConfig,
                      true
                    )
                  }
                ]
              }
            ]
          : [])
      ]
    : [];

  const createChartOptions = useCallback(
    pCotacoes => {
      const to = new Date().getTime();
      const from = new Date(to - 365 * 24 * 3600 * 1000).getTime(); // 1 ano

      const xCotacaoTipos = arrays.arrayToObject(Object.values(COTACAO_TIPO), 'id');
      const xSeries = pCotacoes.map((pCotacao, pIndex) => {
        const xData = pCotacao.data;
        return {
          // Apenas a primeira série precisa ter id para os flags funcionarem
          ...(pIndex === 0 && {
            id: 'historico'
          }),
          name: pCotacao.indicador,
          data: formatIndicadorCotacaoData(xData, xDisplayConfig),
          connectEnds: true,
          type: 'line'
        };
      });

      // Adiciona flags apenas se houver cenários válidos e pelo menos uma série
      if (!validators.isNull(cenarios) && !validators.isEmpty(cenarios) && xSeries.length > 0) {
        const xFlagsData = cenarios
          .filter(xItem => !validators.isNull(xItem.inicio))
          .map(xItem => {
            const { cenario, descricao, inicio, fim, cenario_id } = xItem;
            const xDate = dates.toDate(inicio);
            if (isNaN(xDate.getTime())) {
              return null;
            }
            return {
              x: xDate.getTime(),
              title: cenario || '',
              text: descricao || '',
              // Inclui dados adicionais do cenário para uso no tooltip
              metadata: {
                cenario_id: cenario_id || '',
                inicio: inicio || '',
                fim: fim || '',
                cenario: cenario || '',
                descricao: descricao || ''
              }
            };
          })
          .filter(xItem => !validators.isNull(xItem));

        // Só adiciona flags se houver dados válidos
        if (xFlagsData.length > 0) {
          // Garante que a primeira série tenha um id para os flags funcionarem
          if (!xSeries[0].id) {
            xSeries[0].id = 'historico';
          }
          const xFlags = {
            type: 'flags',
            //onSeries: xSeries[0].id, // Usa o id da primeira série
            shape: 'squarepin',
            name: 'Cenários',
            allowOverlapX: true,
            boostThreshold: 0,
            // Desabilita boost para flags
            zIndex: 100,
            // Garante que os flags apareçam acima das outras séries
            visible: true,
            // Garante que os flags estejam visíveis
            data: xFlagsData,
            // Customização do tooltip
            tooltip: {
              shared: false,
              // Tooltip específico para flags, não compartilhado
              // Use formatter para controle total do tooltip
              formatter: function () {
                // No contexto do formatter, this.point se refere ao ponto do flag
                const xPoint = this.point;
                const xMetadata = xPoint.metadata || {};
                const xTitle = xPoint.title || '';
                const xText = xPoint.text || '';
                const xInicio = xMetadata.inicio
                  ? formats.formatDateCustom(xMetadata.inicio, 'DD/MMM/YY')
                  : '';
                const xFim = xMetadata.fim
                  ? formats.formatDateCustom(xMetadata.fim, 'DD/MMM/YY')
                  : '';
                const xPeriodo = xFim && xInicio !== xFim ? `${xInicio} - ${xFim}` : xInicio;
                let xTooltipContent = `<b>${xTitle}</b><br/>`;
                if (xText) {
                  xTooltipContent += `<span style="color: #666;">${xText}</span><br/>`;
                }
                if (xPeriodo) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.9em;">Período: ${xPeriodo}</span><br/>`;
                }
                if (xMetadata.cenario_id) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.85em;">ID: ${xMetadata.cenario_id}</span>`;
                }
                return xTooltipContent;
              }
            }
          };
          xSeries.push(xFlags);
        }
      }
      const xOptions = {
        title: false,
        chart: {
          zooming: {
            type: 'x'
          },
          events: {
            load: function () {
              const chart = this;
              chart.xAxis[0].setExtremes(from, to);
            }
          }
        },
        rangeSelector: {
          enabled: false,
          buttons: [
            {
              type: 'month',
              count: 1,
              text: '1M'
            },
            {
              type: 'month',
              count: 6,
              text: '6M'
            },
            {
              type: 'year',
              count: 1,
              text: '1A'
            },
            {
              type: 'year',
              count: 2,
              text: '2A'
            },
            {
              type: 'year',
              count: 3,
              text: '3A'
            },
            {
              type: 'year',
              count: 5,
              text: '5A'
            },
            {
              type: 'year',
              count: 10,
              text: '10A'
            },
            {
              type: 'ytd',
              text: 'YTD'
            },
            {
              type: 'all',
              text: 'MÁX.'
            }
          ],
          selected: 2
        },
        xAxis: {
          type: 'datetime',
          crosshair: true
        },
        yAxis: {
          type: 'linear',
          opposite: false,
          title: false,
          labels: {
            formatter: function () {
              return formatIndicadorDisplayValue(this.value, xDisplayConfig);
            }
          }
        },
        plotOptions: {
          series: {
            //gapSize: 7,
          },
          flags: {
            shape: 'squarepin',
            fillColor: '#ffffff',
            allowOverlapX: true,
            zIndex: 100 // Garante que os flags apareçam acima das outras séries
          }
        },
        series: xSeries,
        boost: {
          useGPUTranslations: true
        },
        tooltip: {
          split: false,
          crosshairs: true,
          shared: true,
          formatter: function () {
            // Verifica se algum ponto é um flag (tipo flags)
            const xHasFlag = this.points?.some(pPoint => pPoint.series?.type === 'flags');

            // Se for um flag, usa o formatter específico do flag
            if (xHasFlag) {
              const xFlagPoint = this.points.find(pPoint => pPoint.series?.type === 'flags');
              if (xFlagPoint) {
                const xMetadata = xFlagPoint.point.metadata || {};
                const xInicio = xMetadata.inicio
                  ? formats.formatDateCustom(xMetadata.inicio, 'DD/MMM/YY')
                  : '';
                const xFim = xMetadata.fim
                  ? formats.formatDateCustom(xMetadata.fim, 'DD/MMM/YY')
                  : '';
                const xPeriodo = xFim && xInicio !== xFim ? `${xInicio} - ${xFim}` : xInicio;
                let xTooltipContent = `<b>${xFlagPoint.point.title || ''}</b><br/>`;
                if (xFlagPoint.point.text) {
                  xTooltipContent += `<span style="color: #666;">${xFlagPoint.point.text}</span><br/>`;
                }
                if (xPeriodo) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.9em;">Período: ${xPeriodo}</span><br/>`;
                }
                if (xMetadata.cenario_id) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.85em;">ID: ${xMetadata.cenario_id}</span>`;
                }
                return xTooltipContent;
              }
            }
            const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YY');
            let xTooltipContent = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;
            this.points &&
              this.points.forEach(function (pPoint) {
                const xMetadata = pPoint.point.metadata || {};
                const xCotacaoTipo = xCotacaoTipos[xMetadata.cotacao_tipo_id];

                xTooltipContent += `<span style="color:${pPoint.color}">\u25CF</span>${
                  xDisplayConfig.valueLabel
                }: <b>${formatIndicadorDisplayValue(pPoint.y, xDisplayConfig)}</b><br/>`;

                if (xDisplayConfig.valueKey !== 'taxa' && !validators.isNull(xMetadata.taxa)) {
                  xTooltipContent += `<span style="color:${pPoint.color}">\u25CF</span>Taxa: <b>${xMetadata.taxa}%</b><br/>`;
                }

                if (xMetadata.data_divulgacao) {
                  xTooltipContent += `<span style="color:${
                    pPoint.color
                  }">\u25CF</span>Data Divulgação: <b>${formats.formatDateCustom(
                    xMetadata.data_divulgacao,
                    'DD/MMM/YY'
                  )}</b><br/>`;
                }

                if (xCotacaoTipo) {
                  xTooltipContent += `<span style="color:${pPoint.color}">\u25CF</span>Cotação Tipo: <b>${xCotacaoTipo.descricao}</b><br/>`;
                }
              });

            return xTooltipContent;
          }
        }
      };
      return xOptions;
    },
    [cenarios, xDisplayConfig]
  );
  const [options, setOptions] = useState(null);
  const handleRangeSelect = (type, count) => {
    const xChart = chartRef.current;
    const xTo = new Date().getTime();
    let xFrom;
    switch (type) {
      case 'month':
        xFrom = new Date(xTo - count * 30 * 24 * 3600 * 1000).getTime();
        break;
      case 'year':
        xFrom = new Date(xTo - count * 365 * 24 * 3600 * 1000).getTime();
        break;
      case 'ytd':
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        xFrom = startOfYear;
        break;
      case 'all':
        xFrom = null;
        break;
      default:
        return;
    }
    xChart.xAxis[0].setExtremes(xFrom, xTo);
  };
  useEffect(() => {
    const xOptions = createChartOptions(cotacoes);
    setOptions(xOptions);
  }, [cotacoes, createChartOptions]);
  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={520} />;
  }
  if (validators.isEmpty(xHistorico) || validators.isNull(xResumo)) {
    return null;
  }
  if (validators.isNull(options)) {
    return null;
  }
  return (
    <Stack>
      <Card>
        <CardHeader
          disableTypography
          sx={{
            '& .MuiCardHeader-content': {
              minWidth: 0
            }
          }}
          title={
            <Stack
              flexGrow={1}
              direction={{ sm: 'column', md: 'row' }}
              alignItems="flex-start"
              spacing={2}>
              <Stack flexGrow={1} spacing={1} sx={{ width: '100%' }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Stack width={21} flexShrink={0}>
                    <Icon iconName="chart_line" color="primary" size={21} />
                  </Stack>
                  <Typography
                    variant="subtitle1"
                    color="textPrimary"
                    sx={{ fontWeight: 500 }}
                    noWrap>
                    {xDisplayConfig.title}
                  </Typography>
                </Stack>
                {!validators.isEmpty(xResumoCotacao) ? (
                  <ScrollContainer directions={1}>
                    <Container disableGutters maxWidth={false}>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="flex-end" spacing={2}>
                          <Stack width={21} flexShrink={0} />
                          {xResumoCotacao.map((pItem, pIndex) => (
                            <React.Fragment key={pItem.id}>
                              {pIndex > 0 ? <Divider orientation="vertical" flexItem /> : null}
                              <Stack>
                                {pItem.label ? (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ fontWeight: 500 }}
                                    noWrap>
                                    {pItem.label}
                                  </Typography>
                                ) : null}
                                <Stack spacing={0.5}>
                                  {pItem.values.map(pValue => (
                                    <Stack
                                      key={pValue.id}
                                      direction="row"
                                      gap={1}
                                      alignItems="center">
                                      {pValue.description ? (
                                        <>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ fontWeight: 500 }}
                                            noWrap>
                                            {pValue.description}
                                          </Typography>
                                          <Divider flexItem orientation="vertical" />
                                        </>
                                      ) : null}
                                      <Typography
                                        variant="body2"
                                        color="textPrimary"
                                        sx={{ fontWeight: 500 }}
                                        noWrap>
                                        {pValue.currentValue}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Stack>
                            </React.Fragment>
                          ))}
                        </Stack>
                        <Stack m={2}>
                          <Divider />
                        </Stack>
                      </Stack>
                    </Container>
                  </ScrollContainer>
                ) : null}
              </Stack>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack direction="row" alignItems="flex-end" spacing={2} p={2}>
            <Controls
              range={{
                selected: options.rangeSelector.selected,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect
              }}
            />
          </Stack>
          <Stack sx={{ py: 2 }}>
            <StocksChart key="cotacao" ref={chartRef} options={options} />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});
IndicadoresCotacao.displayName = 'IndicadoresCotacao';
export default IndicadoresCotacao;
